import { useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { CheckIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";

export default function NotificationModal({
  open,
  setOpen,
  title,
  desc,
  error,
  primaryButtonText,
  onPrimaryClick,
  onClose,
}) {
  const hasPrimaryAction = Boolean(!error && primaryButtonText && onPrimaryClick);

  useEffect(() => {
    if (open && !hasPrimaryAction) {
      const timer = setTimeout(() => {
        setOpen(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [open, setOpen, hasPrimaryAction]);

  const handleClose = () => {
    // Close first so parent state (`open`) flips before `onClose` runs; otherwise
    // the panel can briefly re-render as the non-error variant during the exit transition.
    setOpen(false);
    onClose?.();
  };

  const handlePrimaryClick = () => {
    setOpen(false);
    onPrimaryClick?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 z-10 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div>
              <div
                className={`mx-auto flex size-12 items-center justify-center rounded-full ${
                  error ? "bg-red-100" : "bg-green-100"
                }`}
              >
                {error ? (
                  <ExclamationCircleIcon
                    aria-hidden="true"
                    className="size-6 text-red-600"
                  />
                ) : (
                  <CheckIcon
                    aria-hidden="true"
                    className="size-6 text-green-600"
                  />
                )}
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{desc}</p>
                </div>
              </div>
            </div>

            {hasPrimaryAction ? (
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex w-full justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 sm:col-start-1"
                >
                  Close
                </button>
                <button
                  type="button"
                  data-autofocus
                  onClick={handlePrimaryClick}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 sm:mt-0"
                >
                  {primaryButtonText}
                </button>
              </div>
            ) : (
              error && (
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex w-full justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                  >
                    Close
                  </button>
                </div>
              )
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
